import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, Zap, BookOpen } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { stockAPI, newsAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import LegalDisclaimer from '../components/LegalDisclaimer';
import PremiumChart from '../components/PremiumChart';
import ShareButtons from '../components/ShareButtons';
import AddToJournalDialog from '../components/AddToJournalDialog';
import FundamentalsCard from './research/FundamentalsCard';
import ConsensusCard from './research/ConsensusCard';
import VolumeCard from './research/VolumeCard';
import InstitutionalCard from './research/InstitutionalCard';
import SentimentCard from './research/SentimentCard';
import UnusualOptionsCard from './research/UnusualOptionsCard';
import CompanyNewsCard from './research/CompanyNewsCard';

const Research = () => {
  const { isPro } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [symbol, setSymbol] = useState(searchParams.get('symbol') || '');
  const [loading, setLoading] = useState(false);
  const [stockData, setStockData] = useState(null);
  const [analystData, setAnalystData] = useState(null);
  const [sentiment, setSentiment] = useState(null);
  const [companyNews, setCompanyNews] = useState([]);
  const [optionsFlow, setOptionsFlow] = useState(null);
  const [journalOpen, setJournalOpen] = useState(false);

  const handleSearch = useCallback(
    async (overrideSymbol) => {
      const s = (overrideSymbol || symbol || '').trim();
      if (!s) return;

      setLoading(true);
      try {
        const symbolUpper = s.toUpperCase();
        setSymbol(symbolUpper);
        setSearchParams({ symbol: symbolUpper }, { replace: true });
        const [quote, analysts, sent, news] = await Promise.all([
          stockAPI.getQuote(symbolUpper),
          stockAPI.getAnalysts(symbolUpper).catch(() => null),
          newsAPI.getSentiment(symbolUpper).catch(() => null),
          newsAPI.getCompanyNews(symbolUpper).catch(() => ({ news: [] })),
        ]);

        setStockData(quote);
        setAnalystData(analysts);
        setSentiment(sent);
        setCompanyNews(news?.news || []);

        if (isPro && quote?.marketCap) {
          try {
            const flow = await stockAPI.getOptionsFlow(symbolUpper, quote.marketCap);
            setOptionsFlow(flow);
          } catch {
            setOptionsFlow(null);
          }
        } else {
          setOptionsFlow(null);
        }
      } catch (error) {
        console.warn('Research load failed:', error);
        toast({ title: 'Error', description: 'Failed to fetch stock data', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    },
    [symbol, isPro, setSearchParams, toast],
  );

  // Auto-search when landing with ?symbol=XYZ (e.g. from shared links or screener)
  useEffect(() => {
    const urlSym = searchParams.get('symbol');
    if (urlSym && urlSym !== stockData?.symbol) {
      handleSearch(urlSym);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Research Tools</h1>
            <p className="text-gray-500">Comprehensive stock research and analysis</p>
          </div>
        </div>

        <Card className="mb-8">
          <CardContent className="p-6">
            <div className="flex gap-4">
              <Input
                data-testid="research-symbol-input"
                placeholder="Enter stock symbol (e.g., AAPL)"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
              />
              <Button
                data-testid="research-search-btn"
                onClick={() => handleSearch()}
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Search size={18} className="mr-2" />
                {loading ? 'Searching...' : 'Research'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {stockData && (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4" data-testid="research-toolbar">
            <div className="flex items-baseline gap-3">
              <span className="font-bold text-xl text-gray-900">{stockData.symbol}</span>
              <span className={`text-sm ${stockData.changePercent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${stockData.price?.toFixed(2)} ({stockData.changePercent >= 0 ? '+' : ''}
                {stockData.changePercent?.toFixed(2)}%)
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setJournalOpen(true)}
                disabled={!isPro}
                data-testid="research-add-to-journal-btn"
              >
                <BookOpen size={14} className="mr-1" />
                {isPro ? 'Add to Journal' : 'Add to Journal (Pro)'}
              </Button>
              <ShareButtons
                symbol={stockData.symbol}
                price={stockData.price}
                changePercent={stockData.changePercent}
              />
            </div>
          </div>
        )}

        {stockData && (
          <div className="mb-8">
            <PremiumChart symbol={stockData.symbol} isPro={isPro} />
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <FundamentalsCard stockData={stockData} />
          <ConsensusCard analystData={analystData} />
          <VolumeCard stockData={stockData} />
          <InstitutionalCard analystData={analystData} isPro={isPro} />
          <SentimentCard sentiment={sentiment} stockData={stockData} />
          <UnusualOptionsCard optionsFlow={optionsFlow} stockData={stockData} isPro={isPro} />
        </div>

        <CompanyNewsCard symbol={stockData?.symbol} news={companyNews} />

        {!isPro && (
          <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                    <Zap className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Unlock All Research Tools</h3>
                    <p className="text-gray-600 text-sm">
                      Get institutional-grade data, quant signals, and options flow
                    </p>
                  </div>
                </div>
                <Button className="bg-purple-600 hover:bg-purple-700">Upgrade to Pro - $19.99/month</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <LegalDisclaimer variant="compact" />
      </div>
      <AddToJournalDialog
        open={journalOpen}
        onOpenChange={setJournalOpen}
        symbol={stockData?.symbol}
        defaultEntryPrice={stockData?.price}
        defaultSide="buy"
        source="research"
      />
    </div>
  );
};

export default Research;
